import CategoriesSearchSection from "./categories/CategoriesSearchSection";
import EmptyState from "../shared/EmptyState";
import ExpandableCategoryItem from "./categories/ExpandableCategoryItem";
import { ApplicationWithId } from "../../interfaces/IApplications";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  Typography
  } from "@mui/material";
import { CategoryWithId } from "../../interfaces/ICategories";
import { EmojiEvents } from "@mui/icons-material";

interface CategoriesListProps {
  categories: CategoryWithId[];
  application: ApplicationWithId;
  presentations: any[];
  searchTerm: string;
  allCollapsed: boolean;
  selectedDays: number[];
  onDayFilterChange: (days: number[]) => void;
  availableDays: number[];
  showOnlyWithPresentations: boolean;
  onShowOnlyWithPresentationsChange: (value: boolean) => void;
  showOnlyWithCustomMusic: boolean;
  onShowOnlyWithCustomMusicChange: (value: boolean) => void;
  onAddPresentation: (categoryId: string) => void;
  onEditPresentation: (presentation: any) => void;
  onDeletePresentation: (presentation: any) => void;
  onSearchChange: (value: string) => void;
  onToggleCollapse: () => void;
  // Paginacja
  totalCategories: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

export default function CategoriesList({
  categories,
  application,
  presentations,
  searchTerm,
  allCollapsed,
  selectedDays,
  onDayFilterChange,
  availableDays,
  showOnlyWithPresentations,
  onShowOnlyWithPresentationsChange,
  showOnlyWithCustomMusic,
  onShowOnlyWithCustomMusicChange,
  onAddPresentation,
  onEditPresentation,
  onDeletePresentation,
  onSearchChange,
  onToggleCollapse,
  // Paginacja
  totalCategories,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}: CategoriesListProps) {
  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 1,
        backgroundColor: "#fff",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: "#2f2f2f",
          p: 2,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <EmojiEvents sx={{ color: "#6366f1", fontSize: 20 }} />
        Kategorie
      </Typography>

      {/* Wyszukiwarka */}
      <CategoriesSearchSection
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        allCollapsed={allCollapsed}
        onToggleCollapse={onToggleCollapse}
        selectedDays={selectedDays}
        onDayFilterChange={onDayFilterChange}
        availableDays={availableDays}
        showOnlyWithPresentations={showOnlyWithPresentations}
        onShowOnlyWithPresentationsChange={onShowOnlyWithPresentationsChange}
        showOnlyWithCustomMusic={showOnlyWithCustomMusic}
        onShowOnlyWithCustomMusicChange={onShowOnlyWithCustomMusicChange}
      />

      {/* Lista kategorii */}
      {categories.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed #ccc",
            borderRadius: 1,
            p: 2,
            mx: 2,
            mb: 2,
          }}
        >
          <EmptyState
            message={
              searchTerm ? "Nie znaleziono pasujących kategorii" : "Brak dostępnych kategorii"
            }
          />
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2 }}>
          {categories.map((category) => (
            <ExpandableCategoryItem
              key={category.id}
              category={category}
              application={application}
              presentations={presentations || []}
              onAddPresentation={onAddPresentation}
              onEditPresentation={onEditPresentation}
              onDeletePresentation={onDeletePresentation}
              forceCollapsed={allCollapsed}
            />
          ))}
        </Box>
      )}

      {/* Paginacja kategorii */}
      {totalCategories > itemsPerPage && (
        <Box
          sx={{
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ color: "#666" }}>
              Kategorie {currentPage * itemsPerPage + 1}-
              {Math.min((currentPage + 1) * itemsPerPage, totalCategories)} z {totalCategories}
            </Typography>

            <Select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              size="small"
              sx={{ minWidth: 80 }}
            >
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              disabled={currentPage === 0}
              onClick={() => onPageChange(currentPage - 1)}
            >
              Poprzednia
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={(currentPage + 1) * itemsPerPage >= totalCategories}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Następna
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
